// API service for connecting to backend
import { API_BASE_URL as PROD_API_BASE_URL } from '@/config/branding'

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? PROD_API_BASE_URL
  : 'http://localhost:8088'

// Abort requests that hang so loading states can never spin forever
const REQUEST_TIMEOUT_MS = 30000          // standard JSON requests
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000   // file uploads (photos/videos) on slow connections

export const MEMORIAL_SAVE_EVENT = 'memorial-save-completed'

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL
    this.token = localStorage.getItem('api_token') || null
  }

  setToken(token) {
    this.token = token
    if (token) {
      localStorage.setItem('api_token', token)
    } else {
      localStorage.removeItem('api_token')
    }
  }

  getToken() {
    return this.token
  }

  clearToken() {
    this.token = null
    localStorage.removeItem('api_token')
  }

  getStoredMemorialVersion(memorialUuid) {
    if (!memorialUuid) return null

    try {
      const stored = localStorage.getItem('memorial_auth')
      if (!stored) return null

      const data = JSON.parse(stored)
      const storedUuid = data.memorialUuid || data.memorialData?.uuid
      if (storedUuid !== memorialUuid) return null

      return data.memorialData?.version ?? null
    } catch (error) {
      console.warn('Could not read the stored memory version:', error)
      return null
    }
  }

  recordMemorialSave(response, memorialUuid) {
    const savedMemorial = response?.memorial
    if (!response?.success || !savedMemorial?.uuid || savedMemorial.version == null) return

    try {
      const stored = localStorage.getItem('memorial_auth')
      if (stored) {
        const data = JSON.parse(stored)
        const storedUuid = data.memorialUuid || data.memorialData?.uuid

        if (storedUuid === savedMemorial.uuid || storedUuid === memorialUuid) {
          data.memorialData = data.memorialData || {}
          data.memorialData.uuid = savedMemorial.uuid
          data.memorialData.version = savedMemorial.version
          localStorage.setItem('memorial_auth', JSON.stringify(data))
        }
      }
    } catch (error) {
      console.warn('Could not store the updated memory version:', error)
    }

    if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
      window.dispatchEvent(new CustomEvent(MEMORIAL_SAVE_EVENT, {
        detail: {
          memorialUuid: savedMemorial.uuid,
          version: savedMemorial.version,
          staleOverwrite: response.staleOverwrite === true,
          overwrittenVersion: response.overwrittenVersion ?? null
        }
      }))
    }
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`

    const headers = { ...(options.headers || {}) }

    if (!(options.body instanceof FormData)) {
      const hasContentType = Object.keys(headers).some(key => key.toLowerCase() === 'content-type')
      if (!hasContentType) {
        headers['Content-Type'] = 'application/json'
      }
    }

    const config = {
      ...options,
      headers
    }

    // Add token to headers if available
    if (this.token) {
      config.headers['pid'] = this.token
    }

    // Time out hung requests — uploads get a longer window than standard requests
    const timeoutMs = options.timeoutMs || (options.body instanceof FormData ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS)
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
    config.signal = controller.signal

    try {
      const response = await fetch(url, config)
      const data = await response.json()

      // Handle non-200 responses
      if (!response.ok) {
        const error = new Error(data.msg || `HTTP error! status: ${response.status}`)
        error.status = response.status
        error.statusText = response.statusText
        error.data = data

        // Handle 401 Unauthorized - user session expired
        if (response.status === 401) {
          // Clear all auth state so the UI no longer believes the user is logged in
          this.clearToken()
          localStorage.removeItem('memorial_auth')

          // Only redirect if we're not already on login page and not on public endpoints
          if (typeof window !== 'undefined' && window.location) {
            const currentPath = window.location.pathname
            const isPublicPath = currentPath.startsWith('/view/') ||
                                currentPath.startsWith('/for/') ||
                                currentPath.startsWith('/donate/') ||
                                currentPath === '/login' ||
                                currentPath === '/'

            if (!isPublicPath) {
              // Store the current path to redirect back after login
              const redirectPath = encodeURIComponent(currentPath)
              const loginUrl = `/login?msg=${encodeURIComponent("It looks like you've been logged out, please login again")}&redirect=${redirectPath}`
              window.location.href = loginUrl
            }
          }
        }

        // Handle 499 Email Validation Required - account exists but is not enabled.
        // Clear auth and send to login so the user is not stuck in a loop with no way out.
        if (response.status === 499) {
          this.clearToken()
          localStorage.clear()
          sessionStorage.clear()
          if (typeof window !== 'undefined' && window.location) {
            const currentPath = window.location.pathname
            if (currentPath !== '/login') {
              window.location.href = `/login?msg=${encodeURIComponent('Your account needs to be verified. Please sign in again.')}`
            }
          }
        }

        throw error
      }

      return data
    } catch (error) {
      console.error('API request failed:', error)
      if (error.name === 'AbortError') {
        const timeoutError = new Error('The request took too long to complete. Please try again.')
        timeoutError.isTimeout = true
        throw timeoutError
      }
      // Add status info if it's a network error
      if (!error.status && error.name === 'TypeError') {
        error.isNetworkError = true
      }
      throw error
    } finally {
      clearTimeout(timeoutId)
    }
  }

  formatErrorMessage(error) {
    if (error.isTimeout) {
      return {
        message: error.message,
        canRetry: true
      }
    }

    if (error.isNetworkError) {
      return {
        message: 'Unable to connect to the server. Please check your internet connection.',
        canRetry: true
      }
    }

    if (error.message !== undefined && error.message !== null && error.message.length > 0) {
      return {
        message: error.message,
        canRetry: true
      }
    }

    switch (error.status) {
      case 400:
        return {
          message: error.data?.msg || 'Please check your information and try again.',
          canRetry: true
        }
      case 401:
        return {
          message: 'There was an authentication issue. Please try again.',
          canRetry: true
        }
      case 403:
        return {
          message: 'Access not permitted. Please try again or contact support.',
          canRetry: false
        }
      case 404:
        return {
          message: 'Service temporarily unavailable. Please try again.',
          canRetry: true
        }
      case 409:
        return {
          message: error.data?.msg || 'This email address is already registered. Please use a different email or try logging in.',
          canRetry: false
        }
      case 429:
        return {
          message: 'Too many attempts. Please wait a moment before trying again.',
          canRetry: true
        }
      case 500:
      case 502:
      case 503:
      case 504:
        return {
          message: 'Our servers are busy right now. Please try again in a few moments.',
          canRetry: true
        }
      default:
        return {
          message: error.data?.msg || 'Something went wrong. Please try again.',
          canRetry: true
        }
    }
  }

  // Auth endpoints
  async signUp(email, password, captcha = null, firstName = null) {
    return this.request('/anon/signup', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        captcha,
        firstName
      })
    })
  }

  async signIn(email, password) {
    return this.request('/anon/signin', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password
      })
    })
  }

  async signOut(token = this.token) {
    return this.request('/user/signout', {
      method: 'POST',
      keepalive: true,
      headers: {
        'pid': token
      }
    })
  }

  async sendPasswordResetEmail(email, captcha = null) {
    return this.request('/anon/password/resend', {
      method: 'POST',
      body: JSON.stringify({
        email,
        captcha
      })
    })
  }

  async resetPassword(email, code, password, passwordConfirm) {
    return this.request('/anon/password/reset', {
      method: 'POST',
      body: JSON.stringify({
        email,
        code,
        password,
        passwordConfirm
      })
    })
  }

  async confirmCode(code, token) {
    return this.request('/user/code/confirm', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      },
      body: JSON.stringify({
        code
      })
    })
  }

  async resendCode(token) {
    return this.request('/user/code/resend', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      }
    })
  }

  async createMemorial(memorialData, token) {
    return this.request('/memory/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      },
      body: JSON.stringify(memorialData)
    })
  }

  async editMemorial(memorialData, token) {
    const payload = { ...memorialData }

    if (payload.uuid && payload.version == null) {
      const storedVersion = this.getStoredMemorialVersion(payload.uuid)
      if (storedVersion != null) {
        payload.version = storedVersion
      }
    }

    const response = await this.request('/memory/edit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      },
      body: JSON.stringify(payload)
    })

    this.recordMemorialSave(response, payload.uuid)
    return response
  }

  async updateProfilePhoto(memorialUuid, profilePhotoUuid, token) {
    return this.request(`/memory/${memorialUuid}/profile-photo/${profilePhotoUuid}`, {
      method: 'PUT',
      headers: {
        'pid': token
      }
    })
  }

  async saveSlideShow(memorialUuid, settings, slides, token) {
    return this.request('/slideshow/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      },
      body: JSON.stringify({
        memorialUuid: memorialUuid,
        settings: settings,
        slides: slides
      })
    })
  }

  async getSlideShow(memorialUuid, token) {
    return this.request(`/slideshow/memory/${memorialUuid}`, {
      method: 'GET',
      headers: {
        'pid': token
      }
    })
  }

  async generateSlideShowVideo(memorialUuid, token) {
    return this.request(`/slideshow/generate/${memorialUuid}`, {
      method: 'POST',
      headers: {
        'pid': token
      }
    })
  }

  async checkSlideShowStatus(memorialUuid, token) {
    return this.request(`/slideshow/status/${memorialUuid}`, {
      method: 'GET',
      headers: {
        'pid': token
      }
    })
  }

  async uploadVideoMessage(memorialUuid, videoFile, title, token) {
    const formData = new FormData()
    formData.append('file', videoFile)
    if (title) {
      formData.append('title', title)
    }
    
    return this.request(`/memory/host/upload/${memorialUuid}`, {
      method: 'POST',
      headers: {
        'pid': token
        // Don't set Content-Type, let browser set it for FormData
      },
      body: formData
    })
  }

  async uploadPhoto(memorialUuid, photoFile, caption, token, options = {}) {
    const isProfilePhoto = options?.isProfilePhoto === true
    const formData = new FormData()
    formData.append('file', photoFile)
    if (caption) {
      formData.append('title', caption)
    }
    
    const uploadResponse = await this.request(`/memory/media/upload/${memorialUuid}`, {
      method: 'POST',
      headers: {
        'pid': token
        // Don't set Content-Type, let browser set it for FormData
      },
      body: formData
    })

    if (isProfilePhoto && uploadResponse?.success && uploadResponse?.media?.uuid) {
      const updateResponse = await this.updateProfilePhoto(memorialUuid, uploadResponse.media.uuid, token)
      if (!updateResponse?.success) {
        return {
          success: false,
          msg: updateResponse?.msg || 'Failed to update profile photo'
        }
      }
    }

    return uploadResponse
  }

  async uploadAudio(memorialUuid, audioFile, caption, token) {
    const formData = new FormData()
    formData.append('file', audioFile)
    formData.append('title', caption || '')

    return this.request(`/memory/audio/upload/${memorialUuid}`, {
      method: 'POST',
      headers: {
        'pid': token
      },
      body: formData
    })
  }

  async getMediaTranscription(memorialSlug, mediaUuid) {
    return this.request(`/public/memory/${encodeURIComponent(memorialSlug)}/media/${encodeURIComponent(mediaUuid)}/transcription`, {
      method: 'GET'
    })
  }

  async updateShareSlug(memorialUuid, shareSlug, token) {
    return this.request(`/memory/share-slug/${memorialUuid}`, {
      method: 'POST',
      headers: {
        'pid': token
      },
      body: JSON.stringify({ shareSlug })
    })
  }

  async requestMemorialDeletion(memorialUuid, token) {
    return this.request('/memory/request-deletion', {
      method: 'POST',
      headers: {
        'pid': token
      },
      body: JSON.stringify({ memorialUuid })
    })
  }

  async cancelMemorialDeletion(memorialUuid, token) {
    return this.request('/memory/cancel-deletion-request', {
      method: 'POST',
      headers: {
        'pid': token
      },
      body: JSON.stringify({ memorialUuid })
    })
  }

  async submitRsvp(shareSlug, payload) {
    return this.request(`/public/memory/${shareSlug}/rsvp`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  }

  async createPaymentIntent(memorialUuid, userUuid, token) {
    return this.request('/memory/payment-intent', {
      method: 'POST',
      headers: {
        'pid': token
      },
      body: JSON.stringify({
        memorialUuid,
        userUuid
      })
    })
  }

  async processPayment(paymentPayload, token) {
    return this.request('/memory/payment', {
      method: 'POST',
      headers: {
        'pid': token
      },
      body: JSON.stringify(paymentPayload)
    })
  }

  async listMemorials() {
    return this.request('/memory/list', {
      method: 'GET',
      headers: {
        'pid': this.token
      }
    })
  }

  async listMemorialsAndContributions() {
    return this.request('/memory/list-all', {
      method: 'GET',
      headers: {
        'pid': this.token
      }
    })
  }

  async getPublicMemorial(slug) {
    //const headers = {
    //  'Cache-Control': 'no-cache, no-store, must-revalidate',
    //  'Pragma': 'no-cache'
    //}
    let headers = {};
    // Include auth token if available to get personalized data
    if (this.token) {
      headers['pid'] = this.token
    }
    return this.request(`/public/memory/${slug}`, {
      method: 'GET',
      headers
    })
  }

  async submitContribution(formData, token) {
    return this.request('/memory/contribution', {
      method: 'POST',
      headers: {
        'pid': token
      },
      body: formData
    })
  }

  async markContributionSeen(contributionId, token) {
    return this.request('/memory/contribution/seen', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      },
      body: JSON.stringify({ contributionId })
    })
  }

  async approveContribution(contributionId, approve, token) {
    return this.request('/memory/contribution/approve', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      },
      body: JSON.stringify({ contributionId, approve })
    })
  }

  async blockContribution(contributionId, token) {
    return this.request('/memory/contribution/block', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      },
      body: JSON.stringify({ contributionId })
    })
  }

  async updateMediaTitle(mediaUuid, title, token) {
    const formData = new FormData()
    formData.append('title', title)

    return this.request(`/memory/media/update/${mediaUuid}`, {
      method: 'POST',
      headers: {
        'pid': token
        // Don't set Content-Type, let browser set it for FormData
      },
      body: formData
    })
  }

  async deleteMedia(mediaUuid, token) {
    return this.request(`/memory/media/delete/${mediaUuid}`, {
      method: 'POST',
      headers: {
        'pid': token
      }
    })
  }

  async getMemorial(uuid) {
    return this.request(`/memory/get/${uuid}`, {
      method: 'GET',
      headers: {
        'pid': this.token
      }
    })
  }

  async updateMemorialStories(memorialUuid, storiesData, token) {
    return this.request(`/memory/stories/update/${memorialUuid}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      },
      body: JSON.stringify(storiesData)
    })
  }

  async updateMemorialFunds(memorialUuid, fundsData, token) {
    return this.request(`/memory/funds/update/${memorialUuid}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      },
      body: JSON.stringify(fundsData)
    })
  }

  async generatePdf(memorialUuid, token) {
    return this.request(`/memory/pdf/generate/${memorialUuid}`, {
      method: 'POST',
      headers: { 'pid': token }
    })
  }

  async batchUpdatePhotos(memorialUuid, photosData, token) {
    return this.request(`/memory/photos/batch-update/${memorialUuid}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      },
      body: JSON.stringify(photosData)
    })
  }

  // Generic GET request
  async get(endpoint) {
    const headers = {}
    if (this.token) {
      headers['pid'] = this.token
    }
    return this.request(endpoint, {
      method: 'GET',
      headers
    })
  }

  // Generic POST request
  async post(endpoint, data) {
    const headers = {}
    if (this.token) {
      headers['pid'] = this.token
    }
    return this.request(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    })
  }

  // Generic PUT request
  async put(endpoint, data) {
    const headers = {}
    if (this.token) {
      headers['pid'] = this.token
    }
    return this.request(endpoint, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data)
    })
  }

  // Generic DELETE request
  async delete(endpoint) {
    const headers = {}
    if (this.token) {
      headers['pid'] = this.token
    }
    return this.request(endpoint, {
      method: 'DELETE',
      headers
    })
  }

  async addContact(memorialUuid, contactData, token) {
    return this.request(`/memory/contact/add/${memorialUuid}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      },
      body: JSON.stringify(contactData)
    })
  }

  async updateContact(memorialUuid, contactUuid, contactData, token) {
    return this.request(`/memory/contact/update/${memorialUuid}/${contactUuid}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      },
      body: JSON.stringify(contactData)
    })
  }

  async resendMemorialInvite(contactUuid, memorialUuid, token) {
    return this.request(`/memory/resend-invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      },
      body: JSON.stringify({ contactUuid, memorialUuid })
    })
  }

  async sendAllMemorialInvites(memorialUuid, token) {
    return this.request(`/memory/send-all-invites`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      },
      body: JSON.stringify({ memorialUuid })
    })
  }

  async listMemorialAdmins(memorialUuid, token) {
    return this.request(`/memory/${memorialUuid}/admins`, {
      method: 'GET',
      headers: { 'pid': token }
    })
  }

  async addMemorialAdmin(memorialUuid, email, token) {
    return this.request(`/memory/${memorialUuid}/admin/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'pid': token },
      body: JSON.stringify({ email })
    })
  }

  async removeMemorialAdmin(memorialUuid, adminUuid, token) {
    return this.request(`/memory/${memorialUuid}/admin/remove`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'pid': token },
      body: JSON.stringify({ adminUuid })
    })
  }

  async cancelMemorialAdminInvite(memorialUuid, inviteToken, token) {
    return this.request(`/memory/${memorialUuid}/admin/invite/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'pid': token },
      body: JSON.stringify({ inviteToken })
    })
  }

  async getAdminInviteInfo(token) {
    return this.request(`/anon/admin-invite/${token}`, {
      method: 'GET'
    })
  }

  async acceptAdminInvite(inviteToken, authToken) {
    return this.request(`/memory/admin-invite/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'pid': authToken },
      body: JSON.stringify({ token: inviteToken })
    })
  }

  async validatePromoCode(code, memorialUuid, token) {
    return this.request('/memory/promo/validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'pid': token
      },
      body: JSON.stringify({ code, memorialUuid })
    })
  }

  async getConnectStatus() {
    return this.request('/stripe/connect/status', {
      method: 'GET',
      headers: { 'pid': this.token }
    })
  }

  async createConnectOnboarding() {
    return this.request('/stripe/connect/onboard', {
      method: 'POST',
      headers: { 'pid': this.token }
    })
  }

  async processDonation(donationData, token = null) {
    const headers = { 'Content-Type': 'application/json' }
    if (token) headers['pid'] = token
    return this.request('/donation/process', {
      method: 'POST',
      headers,
      body: JSON.stringify(donationData)
    })
  }

  async createSponsorship(sponsorshipData, token = null) {
    const headers = { 'Content-Type': 'application/json' }
    if (token) headers['pid'] = token
    return this.request('/sponsorship/create', {
      method: 'POST',
      headers,
      body: JSON.stringify(sponsorshipData)
    })
  }

  async getCharitySummary(charityName) {
    return this.request('/ai/charity-summary', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ charityName })
    })
  }

  /**
   * Submit a support ticket
   * @param {FormData} formData - Form data containing name, email, message, and optional file
   * @returns {Promise} Response with success status and ticket UUID
   */
  async submitSupportTicket(formData) {
    return this.request('/anon/support/submit', {
      method: 'POST',
      body: formData
      // Note: Don't set Content-Type header - browser will set it automatically for FormData with boundary
    })
  }

  // QR code — admin/owner endpoints (authenticated)
  async generateQrCode(memorialUuid, message, token) {
    return this.request(`/memory/qr/generate/${memorialUuid}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', pid: token },
      body: JSON.stringify({ message })
    })
  }

  async getQrCode(memorialUuid, token) {
    return this.request(`/memory/qr/${memorialUuid}`, {
      method: 'GET',
      headers: { pid: token }
    })
  }

  async disableQrCode(memorialUuid, token) {
    return this.request(`/memory/qr/disable/${memorialUuid}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', pid: token },
      body: JSON.stringify({})
    })
  }

  async updateQrMessage(memorialUuid, message, token) {
    return this.request(`/memory/qr/message/${memorialUuid}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', pid: token },
      body: JSON.stringify({ message })
    })
  }

  // QR code — anonymous (no auth required)
  async validateQrToken(token) {
    return this.request(`/anon/qr/${token}`, { method: 'GET' })
  }

  async submitQrContribution(formData) {
    return this.request('/anon/qr/contribute', {
      method: 'POST',
      body: formData
    })
  }

  // Published guides, optionally filtered by category slug or tag
  async listGuides({ category, tag } = {}) {
    const params = new URLSearchParams()
    if (category) params.set('category', category)
    if (tag) params.set('tag', tag)
    const query = params.toString()
    return this.request(`/public/guides${query ? `?${query}` : ''}`, { method: 'GET' })
  }

  async getGuide(slug) {
    return this.request(`/public/guide/${encodeURIComponent(slug)}`, { method: 'GET' })
  }
}

// Create singleton instance
export const apiService = new ApiService()
export default apiService
